import { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Grid,
  Stack, Chip, Avatar, IconButton, Tooltip, Divider,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, Switch,
  CircularProgress, Alert, Paper, MenuItem,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Business as SchoolIcon, Add as AddIcon, Edit as EditIcon,
  Image as LogoIcon, Save as SaveIcon, Close as CloseIcon,
  CloudUpload as UploadIcon, Delete as DeleteIcon,
  Storage as DatabaseIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import { useAuth } from '../../context/AuthContext';

const EMPTY = {
  id: null, code: '', name: '', motto: '', address: '',
  phone: '', email: '', region: '', logo: '', report_header: '', active: 1,
};

// Administrator designation, used only when creating a school.
// 'none' is allowed so a school can be created now and staffed later
// from the Users page.
const EMPTY_ADMIN = {
  mode: 'new',            // 'new' | 'existing' | 'none'
  user_id: '',
  first_name: '', last_name: '', email: '', username: '', password: '',
};

// `requestMode` turns the same form into a request: identical fields
// (a request has to carry everything, so approval needs no further
// input) but it posts to the approval queue instead of creating the
// school outright.
function SchoolEditor({ open, initial, onClose, onSaved, requestMode = false }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [admin, setAdmin] = useState(EMPTY_ADMIN);
  const [users, setUsers] = useState([]);
  const isNew = !form.id;

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
      setAdmin(EMPTY_ADMIN);
    }
  }, [open, initial]);

  // Existing accounts, for the "point at an account that already
  // exists" option. Only needed when creating.
  useEffect(() => {
    if (!open || initial) return;
    API.get('/users')
      .then(r => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setUsers([]));
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({
    ...f,
    [k]: e?.target ? e.target.value : e,
  }));

  const readLogo = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: String(reader.result || '') }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.code?.trim() || !form.name?.trim()) {
      swal.warning('Code and Name are required.');
      return;
    }
    // Validate the administrator block before we create anything, so a
    // typo doesn't leave a school standing with no admin.
    if (isNew && admin.mode === 'new') {
      if (!admin.first_name?.trim() || !admin.last_name?.trim()
          || !admin.email?.trim() || !admin.password) {
        swal.warning('Fill in the administrator\'s name, email and password — '
                   + 'or switch to "Use an existing account" / "Decide later".');
        return;
      }
      if (admin.password.length < 6) {
        swal.warning('Administrator password must be at least 6 characters.');
        return;
      }
    }
    if (isNew && admin.mode === 'existing' && !admin.user_id) {
      swal.warning('Pick the account that will administer this school.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        code:          form.code.trim(),
        name:          form.name.trim(),
        motto:         form.motto?.trim() || null,
        address:       form.address?.trim() || null,
        phone:         form.phone?.trim() || null,
        email:         form.email?.trim() || null,
        region:        form.region?.trim() || null,
        logo:          form.logo || null,
        report_header: form.report_header?.trim() || null,
        active:        form.active ? 1 : 0,
      };
      if (form.id) {
        await API.put(`/schools/${form.id}`, payload);
      } else {
        // Creating a school also provisions its database (a structural
        // clone of School #1 with no rows). That can take several
        // seconds, and can partially fail — the API answers 207 with
        // the reason when it does, and the row stays retryable via the
        // Provision action on the list.
        // Designate the school's administrator in the same request, so
        // nobody is ever sent through the first-run setup wizard for a
        // school the owner created.
        if (admin.mode === 'existing') {
          payload.admin_user_id = Number(admin.user_id);
        } else if (admin.mode === 'new') {
          payload.admin = {
            first_name: admin.first_name.trim(),
            last_name:  admin.last_name.trim(),
            email:      admin.email.trim(),
            username:   admin.username?.trim() || undefined,
            password:   admin.password,
          };
        }

        if (requestMode) {
          await API.post('/schools/requests', payload);
          swal.success(
            'Your request has been sent for approval. As soon as it is approved, '
            + 'the school, its private database and its administrator are created '
            + 'automatically — nothing else for you to do.',
            'Request submitted'
          );
          onSaved?.();
          onClose?.();
          return;
        }

        const res = await API.post('/schools', payload);
        if (res.status === 207 || res.data?.provisioned === false) {
          swal.warning(
            res.data?.error
              ? `School created, but its database was not fully built: ${res.data.error}`
              : 'School created, but its database was not fully built. Use "Repair database" on the list to retry.',
            'Database not ready'
          );
        } else {
          const t = res.data?.provision?.steps?.tables?.created?.length;
          const a = res.data?.admin;
          swal.success(
            `Database "${res.data?.db_name}" built`
            + (t ? ` with ${t} tables` : '') + '. '
            + (a
                ? `${a.user.first_name} ${a.user.last_name} `
                  + `${a.mode === 'created' ? 'can now sign in' : 'now administers'} this school.`
                : 'Assign an administrator from the Users page.'),
            'School ready'
          );
        }
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      swal.error(swal.pickError(e, 'Save failed'), 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, extra = {}) => (
    <TextField
      label={label}
      value={form[key] ?? ''}
      onChange={set(key)}
      fullWidth
      size="small"
      variant="filled"
      InputLabelProps={{ shrink: true }}
      InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
      {...extra}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SchoolIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            {requestMode ? 'Request a School' : form.id ? 'Edit School' : 'New School'}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 2 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4}>
            {field('code', 'Code', { required: true, placeholder: 'MAIN, PRESCH' })}
          </Grid>
          <Grid item xs={12} sm={8}>
            {field('name', 'School Name', { required: true, placeholder: 'e.g. Preschool' })}
          </Grid>
          <Grid item xs={12} sm={6}>{field('motto', 'Motto')}</Grid>
          <Grid item xs={12} sm={6}>{field('region', 'Region')}</Grid>
          <Grid item xs={12}>{field('address', 'Address', { multiline: true, minRows: 2 })}</Grid>
          <Grid item xs={12} sm={6}>{field('phone', 'Phone')}</Grid>
          <Grid item xs={12} sm={6}>{field('email', 'Email')}</Grid>
          <Grid item xs={12}>
            {field('report_header', 'Report Header', {
              multiline: true, minRows: 2,
              helperText: 'Optional — shown at the top of printed reports.',
            })}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }}>Logo</Divider>
            <Stack direction="row" spacing={2} alignItems="center">
              {form.logo
                ? <Avatar src={form.logo} variant="rounded" sx={{ width: 72, height: 72 }} />
                : <Avatar variant="rounded" sx={{ width: 72, height: 72 }}><LogoIcon /></Avatar>}
              <Stack direction="row" spacing={1}>
                <Button component="label" size="small" startIcon={<UploadIcon />} variant="outlined">
                  Upload
                  <input
                    type="file" accept="image/*" hidden
                    onChange={(e) => readLogo(e.target.files?.[0])}
                  />
                </Button>
                {form.logo && (
                  <Button size="small" color="error" startIcon={<DeleteIcon />}
                    onClick={() => setForm((f) => ({ ...f, logo: '' }))}>
                    Remove
                  </Button>
                )}
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={!!form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked ? 1 : 0 }))}
              />
              <Typography variant="body2" color="text.secondary">
                Active — users of this school can log in
              </Typography>
            </Stack>
          </Grid>

          {/* Administrator — creation only. Naming the admin here is
              what keeps a new school from ever showing the first-run
              setup wizard: it arrives already set up, with someone who
              can sign in. */}
          {isNew && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}>School Administrator</Divider>
              <TextField
                select fullWidth size="small" variant="filled"
                label="Who will run this school?"
                value={admin.mode}
                onChange={(e) => setAdmin(a => ({ ...a, mode: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                sx={{ mb: 1.5 }}
              >
                <MenuItem value="new">Create a new administrator account</MenuItem>
                <MenuItem value="existing">Use an account that already exists</MenuItem>
                <MenuItem value="none">Decide later</MenuItem>
              </TextField>

              {admin.mode === 'new' && (
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField label="First Name" required fullWidth size="small" variant="filled"
                      value={admin.first_name} InputLabelProps={{ shrink: true }}
                      InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                      onChange={(e) => setAdmin(a => ({ ...a, first_name: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Last Name" required fullWidth size="small" variant="filled"
                      value={admin.last_name} InputLabelProps={{ shrink: true }}
                      InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                      onChange={(e) => setAdmin(a => ({ ...a, last_name: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Email" type="email" required fullWidth size="small" variant="filled"
                      value={admin.email} InputLabelProps={{ shrink: true }}
                      InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                      onChange={(e) => setAdmin(a => ({ ...a, email: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField label="Username" fullWidth size="small" variant="filled"
                      value={admin.username} InputLabelProps={{ shrink: true }}
                      helperText="Optional — defaults to the email"
                      InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                      onChange={(e) => setAdmin(a => ({ ...a, username: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField label="Initial Password" required fullWidth size="small" variant="filled"
                      value={admin.password} InputLabelProps={{ shrink: true }}
                      helperText="At least 6 characters — they'll be asked to change it on first login"
                      InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                      onChange={(e) => setAdmin(a => ({ ...a, password: e.target.value }))} />
                  </Grid>
                </Grid>
              )}

              {admin.mode === 'existing' && (
                <TextField
                  select fullWidth size="small" variant="filled" label="Account"
                  value={admin.user_id}
                  onChange={(e) => setAdmin(a => ({ ...a, user_id: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  helperText="This account moves to the new school and is given Super Administrator there"
                  InputProps={{ disableUnderline: true, sx: { borderRadius: 1.5 } }}
                >
                  {users.map(u => (
                    <MenuItem key={u.id} value={String(u.id)}>
                      {u.first_name} {u.last_name} — {u.email}
                      {u.school_name ? ` (now in ${u.school_name})` : ''}
                    </MenuItem>
                  ))}
                  {!users.length && <MenuItem value="" disabled>No accounts available</MenuItem>}
                </TextField>
              )}

              <Alert severity="info" variant="outlined" sx={{ mt: 1.5, borderRadius: 2 }}>
                This administrator runs <strong>only this school</strong>. They will not be able
                to create or edit schools — that stays with the owner account.
              </Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} startIcon={<CloseIcon />}>Cancel</Button>
        <Button
          onClick={save} variant="contained" disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {saving ? 'Saving…' : requestMode ? 'Send Request' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Colour + wording for each request state.
const REQUEST_STATUS = {
  pending:   { color: 'warning', label: 'Awaiting approval' },
  approved:  { color: 'info',    label: 'Approved — building' },
  fulfilled: { color: 'success', label: 'Created' },
  rejected:  { color: 'error',   label: 'Declined' },
};

export default function SchoolsPage() {
  const { canManageSchools, canCreateSchoolDirectly } = useAuth();
  const [requests, setRequests] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const [controlDb, setControlDb] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [provisioning, setProvisioning] = useState(null); // school id in flight

  const reload = useCallback(async () => {
    setLoading(true); setError('');

    // The request queue is always available to the owner, even before
    // the multi-school module is switched on — requesting is how it
    // gets switched on. Fetching it also nudges the server to build
    // anything that has just been approved.
    try {
      const rq = await API.get('/schools/requests');
      setRequests(rq.data?.requests || []);
    } catch { setRequests([]); }

    // The management list only exists once the module is live.
    if (!canManageSchools) { setLoading(false); return; }
    try {
      const res = await API.get('/schools');
      setSchools(res.data?.schools || []);
      setControlDb(res.data?.control_database || '');
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [canManageSchools]);

  useEffect(() => { reload(); }, [reload]);

  const openNew  = () => { setEditing(null); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setOpen(true); };

  // Build or repair a school's database. Idempotent on the server, so
  // it is safe to run again after a failure or after School #1's
  // schema has moved on.
  const provision = async (s) => {
    const confirmed = await swal.confirm({
      title: s.provisioned_at ? 'Repair database' : 'Build database',
      text: s.provisioned_at
        ? `Re-sync "${s.name}" against School #1's current schema? `
          + 'Existing data is preserved — only missing tables, columns and views are added.'
        : `Build the database for "${s.name}"? It will be a structural copy of `
          + 'School #1 with no records in it.',
      confirmText: s.provisioned_at ? 'Re-sync now' : 'Build it',
      tone: s.provisioned_at ? 'warning' : 'primary',
    });
    if (!confirmed) return;

    setProvisioning(s.id);
    try {
      const res = await API.post(`/schools/${s.id}/provision`);
      const steps = res.data?.provision?.steps || {};
      swal.success(
        `Database "${res.data?.db_name}" ready — `
        + `${steps.tables?.created?.length || 0} tables, `
        + `${steps.controlViews?.created?.length || 0} shared views.`,
        'Provisioned'
      );
      reload();
    } catch (e) {
      swal.error(swal.pickError(e, 'Provisioning failed'), 'Provisioning failed');
    } finally {
      setProvisioning(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main' }}><SchoolIcon /></Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" fontWeight={800}>Schools</Typography>
          <Typography variant="caption" color="text.secondary">
            Each school here operates as its own tenant — separate data, users, reports and branding.
          </Typography>
        </Box>
        {/* Requesting is open to every main-school super administrator.
            Creating outright skips the vendor's approval, so that button
            only appears for the master owner. */}
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRequestOpen(true)}>
            Request a School
          </Button>
          {canCreateSchoolDirectly && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={openNew}>
              New School
            </Button>
          )}
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {canManageSchools ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Every school gets <strong>its own database</strong>, so one school can
          never see another's students, fees or payroll. School&nbsp;#1 uses{' '}
          <code>{controlDb || 'the database named in .env'}</code>, which also
          holds the shared logins, roles and audit trail. New schools are built
          as an empty structural copy of School&nbsp;#1.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Multi-school isn't switched on for this installation yet.{' '}
          <strong>Request a school</strong> with its full details and who will run
          it. Once the request is approved, the school, its own private database
          and its administrator are created <strong>automatically</strong> —
          there is nothing further to set up and no wizard to run again.
        </Alert>
      )}

      {/* The request queue. Always shown to the owner: before activation
          it is the whole page, afterwards it is the history of how each
          school came to exist. */}
      {!!requests.length && (
        <Card sx={{ mb: 3 }}>
          <Box sx={{ px: 2, py: 1.2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" fontWeight={800}>School Requests</Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>School</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Administrator</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Requested</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => {
                  const s = REQUEST_STATUS[r.status] || { color: 'default', label: r.status };
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.code}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {r.admin?.email || (r.admin?.user_id ? `User #${r.admin.user_id}` : '—')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {r.requested_at ? new Date(r.requested_at).toLocaleString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={s.color} label={s.label} sx={{ fontWeight: 700 }} />
                        {r.error && (
                          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.4 }}>
                            {r.error}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {!canManageSchools && !requests.length && !loading && (
        <Card sx={{ p: 5, textAlign: 'center' }}>
          <Avatar sx={{ bgcolor: 'primary.main', mx: 'auto', mb: 2 }}><SchoolIcon /></Avatar>
          <Typography variant="h6" fontWeight={700} gutterBottom>No requests yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460, mx: 'auto', mb: 2 }}>
            Use <strong>Request a School</strong> to enter the new school's name,
            logo and contact details, and name the person who will administer it.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setRequestOpen(true)}>
            Request a School
          </Button>
        </Card>
      )}

      {canManageSchools && (
      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Logo</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Region</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Database</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schools.map((s) => (
                    <TableRow key={s.id} hover>
                      <TableCell>{s.id}</TableCell>
                      <TableCell>
                        {s.logo
                          ? <Avatar src={s.logo} variant="rounded" sx={{ width: 32, height: 32 }} />
                          : <Avatar variant="rounded" sx={{ width: 32, height: 32 }}><LogoIcon fontSize="small" /></Avatar>}
                      </TableCell>
                      <TableCell><Chip size="small" label={s.code} /></TableCell>
                      <TableCell>{s.name}</TableCell>
                      <TableCell>{s.region || '—'}</TableCell>
                      <TableCell>
                        {s.db_name ? (
                          <Stack spacing={0.25}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {s.db_name}
                            </Typography>
                            {Number(s.id) === 1 ? (
                              <Chip size="small" variant="outlined" label="Main database" sx={{ height: 18, fontSize: 10 }} />
                            ) : s.provisioned_at ? (
                              <Chip size="small" color="success" variant="outlined" label="Provisioned" sx={{ height: 18, fontSize: 10 }} />
                            ) : (
                              <Chip size="small" color="warning" label="Not built" sx={{ height: 18, fontSize: 10 }} />
                            )}
                          </Stack>
                        ) : (
                          <Chip size="small" color="warning" label="No database yet" sx={{ height: 20, fontSize: 10.5 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {s.active
                          ? <Chip size="small" color="success" label="Active" />
                          : <Chip size="small" color="default" label="Inactive" />}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {Number(s.id) !== 1 && (
                            <Tooltip title={s.provisioned_at
                              ? "Re-sync this school's database against School #1's schema"
                              : 'Build this school\'s database now'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color={s.provisioned_at ? 'default' : 'warning'}
                                  disabled={provisioning === s.id}
                                  onClick={() => provision(s)}
                                >
                                  {provisioning === s.id
                                    ? <CircularProgress size={16} />
                                    : <DatabaseIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit school">
                            <IconButton size="small" onClick={() => openEdit(s)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!schools.length && (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        No schools yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      )}

      <SchoolEditor
        open={open} initial={editing}
        onClose={() => setOpen(false)}
        onSaved={reload}
      />

      {/* Same form, but it goes into the approval queue. */}
      <SchoolEditor
        requestMode
        open={requestOpen} initial={null}
        onClose={() => setRequestOpen(false)}
        onSaved={reload}
      />
    </Box>
  );
}
